/*
 * Project: bellcloud
 * File: apps/frontend/src/components/device/connectDevice/ConnectionBadge.tsx
 * Description: Connection type badge component (Cradle/Bluetooth with checkmark)
 * Uses global connection store - no props needed
 */

import React from 'react';
import styled from '@emotion/styled';
import { useConnectionStore } from '../../../store/connectionStore';

interface ConnectionBadgeProps {
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * ConnectionBadge Component
 *
 * Displays connection type badge with checkmark from global store:
 * - Cradle (purple border)
 * - Bluetooth (purple border)
 * - Checkmark when connected
 * - Automatically reads from useConnectionStore
 */
export const ConnectionBadge: React.FC<ConnectionBadgeProps> = ({ size = 'md' }) => {
  const { protocol, adapter } = useConnectionStore();

  if (protocol.type === 'none') {
    return null;
  }

  let displayText: string;
  let isConnected = false;
  if (adapter.type === 'cradle' || adapter.type === 'dock') {
    displayText = 'Cradle';
    isConnected = true;
  } else if (protocol.type === 'ble') {
    displayText = 'Bluetooth';
    isConnected = true;
  } else {
    return null;
  }

  return (
    <Badge $size={size} $isConnected={isConnected}>
      {isConnected && <CheckIcon>✓</CheckIcon>}
      <BadgeText>{displayText}</BadgeText>
    </Badge>
  );
};

// ==================== Styled Components ====================

const Badge = styled.div<{ $size: 'sm' | 'md'; $isConnected: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing[2]}px;
  gap: ${({ theme, $size }) => ($size === 'sm' ? theme.spacing[1] : theme.spacing[2])}px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  border: 1.5px solid ${({ theme }) => theme.colors.button.stroke.tertiaryBrand.default};
  background-color: ${({ theme }) => theme.colors.surface.group.brand};
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.button.text.tertiaryBrand};
`;

const BadgeText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;
